import createClient, { type Client } from 'openapi-fetch'
import type { components, paths } from './api'

/**
 * The generated API client (CLAUDE.md: front-ends use only this). Types come
 * from the server's own OpenAPI document (`pnpm api:generate`); openapi-fetch
 * is a thin typed fetch, so no schema library ships to the browser.
 */
export type ApiClient = Client<paths>
export type ApiSchemas = components['schemas']
export type { paths as ApiPaths }

export interface ApiClientOptions {
  /** Where the API answers, e.g. https://api.example.org (no trailing slash needed). */
  baseUrl: string
  /** `<app>/<MAJOR.MINOR.PATCH>`, e.g. `booking/0.1.0`; below the minimum the API answers 426. */
  appVersion: string
  /**
   * The public apps send no cookies: their tokens travel in headers ('omit').
   * The staff web app is served from the API's own origin and authenticates
   * with httpOnly cookies, so it passes 'same-origin'; never 'include'. The
   * staff iPad app sends its credentials in headers through its own `fetch`
   * (A1), with 'omit'.
   */
  credentials?: 'omit' | 'same-origin'
  fetch?: (input: Request) => Promise<Response>
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  return createClient<paths>({
    baseUrl: trimSlash(options.baseUrl),
    headers: { 'x-app-version': options.appVersion },
    credentials: options.credentials ?? 'omit',
    ...(options.fetch ? { fetch: options.fetch } : {}),
  })
}

export type UploadLink = ApiSchemas['DocumentUploadResponse']['upload']

/**
 * Sends a file to an upload link from POST /bookings/{code}/documents. A link
 * on this API is relative and needs the app version; a storage provider's
 * signed URL (later, S3) is absolute and gets only the headers it came with.
 * The staff app's links (W2) also need its session: the web app's cookies
 * ('same-origin', and only for a link on its own API), or the iPad app's
 * header, added by its own `fetch` (A1).
 */
export async function putUpload(
  link: UploadLink,
  file: Blob,
  options: Pick<ApiClientOptions, 'baseUrl' | 'appVersion' | 'fetch' | 'credentials'> & {
    /** Gives up on the upload (S4: a send with no answer in time). */
    signal?: AbortSignal
  },
): Promise<Response> {
  const relative = link.url.startsWith('/')
  const url = relative ? `${trimSlash(options.baseUrl)}${link.url}` : link.url
  const headers = new Headers(link.headers)
  if (relative) headers.set('x-app-version', options.appVersion)
  const request = new Request(url, {
    method: link.method,
    headers,
    body: file,
    credentials: relative ? (options.credentials ?? 'omit') : 'omit',
    ...(options.signal ? { signal: options.signal } : {}),
  })
  return (options.fetch ?? fetch)(request)
}

function trimSlash(url: string): string {
  return url.replace(/\/+$/, '')
}

/** Why a call failed: the network, or an API answer in the one error shape. */
export type ApiProblem =
  | { kind: 'network' }
  | { kind: 'api'; status: number; code: string; details: Record<string, unknown> }

export type Result<T> = { ok: true; data: T } | { ok: false; problem: ApiProblem }

type FetchResult<T> = { data?: T; error?: unknown; response: Response }

/** Turns an openapi-fetch call into a Result; a rejected fetch is a network problem. */
export async function call<T>(request: Promise<FetchResult<T>>): Promise<Result<T>> {
  let result: FetchResult<T>
  try {
    result = await request
  } catch {
    return { ok: false, problem: { kind: 'network' } }
  }
  // 204 No Content has no body: openapi-fetch gives `data` undefined, but it worked.
  if (result.response.ok && (result.data !== undefined || result.response.status === 204)) {
    return { ok: true, data: result.data as T }
  }
  return { ok: false, problem: toProblem(result.response.status, result.error) }
}

export function toProblem(status: number, body: unknown): ApiProblem {
  const error = body as Partial<ApiSchemas['Error']> | undefined
  return {
    kind: 'api',
    status,
    code: typeof error?.code === 'string' ? error.code : 'internal_error',
    details: error?.details && typeof error.details === 'object' ? error.details : {},
  }
}

/** A fresh Idempotency-Key; crypto.randomUUID needs a secure context, so fall back to random bytes. */
export function newIdempotencyKey(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}
